import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'

import { gsap, useGsap, prefersReducedMotion } from '@/lib/gsap'
import { NomaMark } from '@/components/generals/noma-mark'

/* Five columns sweep down to cover the outgoing page and lift away once the
   next one has mounted, with the mark holding the middle of the wipe. */
export const PageTransition: FC = (): ReactNode => {
    const scope = useRef<HTMLDivElement>(null)
    const covering = useRef<gsap.core.Timeline | null>(null)
    const waiting = useRef(false)
    const router = useRouter()

    useGsap(() => {
        gsap.set(scope.current, { autoAlpha: 0 })
        gsap.set('[data-transition-panel]', { scaleY: 0 })
    }, scope, [])

    useEffect(() => {
        if (prefersReducedMotion()) {
            return
        }

        const lift = (): void => {
            const tl = gsap.timeline({
                onComplete: () => {
                    gsap.set(scope.current, { autoAlpha: 0 })
                    gsap.set('[data-transition-panel]', { scaleY: 0 })
                }
            })

            tl.to('[data-transition-mark]', {
                opacity: 0,
                scale: 0.6,
                duration: 0.3,
                ease: 'power2.in'
            }, 0)

            tl.to('[data-transition-panel]', {
                scaleY: 0,
                transformOrigin: 'top',
                duration: 0.72,
                stagger: 0.05,
                ease: 'noma'
            }, 0.1)

            covering.current = tl

            window.scrollTo(0, 0)
        }

        const cover = (): void => {
            waiting.current = false
            covering.current?.kill()

            const tl = gsap.timeline({
                onComplete: () => {
                    /* The next page was already ready before the curtain
                       finished closing, so lift it now rather than leaving it
                       stuck at full height. */
                    if (waiting.current) {
                        waiting.current = false
                        lift()
                    }
                }
            })

            tl.set(scope.current, { autoAlpha: 1 })

            tl.to('[data-transition-panel]', {
                scaleY: 1,
                transformOrigin: 'bottom',
                duration: 0.62,
                stagger: 0.05,
                ease: 'noma'
            }, 0)

            tl.fromTo('[data-transition-mark]',
                { opacity: 0, scale: 0.5, rotate: -25 },
                { opacity: 1, scale: 1, rotate: 0, duration: 0.5, ease: 'noma' },
                0.28
            )

            covering.current = tl
        }

        /* A client-side route resolves in a few milliseconds, far quicker than
           the 0.82s the curtain takes to close. This used to kill the closing
           timeline and reverse from wherever it had reached, so the wipe never
           finished and read as a half-played animation. It now waits for the
           cover to land before lifting. */
        const reveal = (): void => {
            const tl = covering.current

            if (tl && tl.isActive()) {
                waiting.current = true

                return
            }

            lift()
        }

        router.events.on('routeChangeStart', cover)
        router.events.on('routeChangeComplete', reveal)
        router.events.on('routeChangeError', reveal)

        return () => {
            router.events.off('routeChangeStart', cover)
            router.events.off('routeChangeComplete', reveal)
            router.events.off('routeChangeError', reveal)
            covering.current?.kill()
        }
    }, [router.events])

    return (
        <div
            ref={scope}
            className='pointer-events-none invisible fixed inset-0 z-[125] flex items-center justify-center'
            aria-hidden='true'
        >
            <div className='absolute inset-0 flex'>
                {[0, 1, 2, 3, 4].map((panel) => (
                    <span
                        key={panel}
                        data-transition-panel
                        className='h-full flex-1 bg-ink'
                    />
                ))}
            </div>

            <span data-transition-mark className='relative text-brand opacity-0'>
                <NomaMark className='size-8' />
            </span>
        </div>
    )
}
